import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Progress,
  Row,
  Select,
  Space,
  Tag,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import {
  useStockReconciliationHistory,
  useStockReconciliationQueue,
} from '@/api/StockReconciliation';
import { QUERY_KEYS } from '@/api/queryKeys';
import { queryClient } from '@/config/reactQuery';
import { useAppCustomers, useHttpAuthReady } from '@/hooks';
import { useTranslate } from '@/translations';
import { StockReconciliationHistoryItem } from '@/types';
import {
  CustomColumns,
  CustomTable,
  PageContainer,
  Title,
  faClipboardCheck,
} from '@/ui/components';
import { SelectedFile } from './StockReconciliation.styled';
import { ParsedStockFile, parseStockFile } from './parseStockFile';
import { StockUploadColumns } from './prepareStockUploadRows';

type SetupValues = {
  customerId: number;
  locationId: number;
  reconciliationAt: Dayjs;
  vinColumn?: string;
  locationColumn?: string;
  modelColumn?: string;
};

type StockReconciliationSetupProps = {
  submitting?: boolean;
  submitLabel?: string;
  error?: string;
  onSubmit: (values: {
    customerId: number;
    customerName: string;
    locationId: number;
    locationName: string;
    reconciliationAt: string;
    file: File;
    rows: Record<string, string>[];
    columns: StockUploadColumns;
  }) => void;
  onOpenRun: (id: number) => void;
};

export const StockReconciliationSetup: React.FC<
  StockReconciliationSetupProps
> = ({ submitting, submitLabel, error, onSubmit, onOpenRun }) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const isHttpAuthReady = useHttpAuthReady();
  const [form] = Form.useForm<SetupValues>();
  const vinColumn = Form.useWatch('vinColumn', form);
  const locationColumn = Form.useWatch('locationColumn', form);
  const modelColumn = Form.useWatch('modelColumn', form);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsed, setParsed] = useState<ParsedStockFile>();
  const [parsing, setParsing] = useState(false);
  const parseRequest = useRef(0);
  const [historyCustomerId, setHistoryCustomerId] = useState<number>();
  const [historyLocationId, setHistoryLocationId] = useState<number>();
  const [historyPage, setHistoryPage] = useState(1);
  const historyPageSize = 10;
  const {
    userPermissions: { customers = [], locations = [] },
  } = useAppCustomers();
  const { data: queue } = useStockReconciliationQueue({
    enabled: isHttpAuthReady,
  });
  const previousQueueIds = useRef<number[]>([]);
  useEffect(() => {
    const currentIds = (queue?.items ?? []).map((item) => item.id);
    const completed = previousQueueIds.current.filter(
      (id) => !currentIds.includes(id),
    );
    previousQueueIds.current = currentIds;
    if (completed.length) {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.stockReconciliationHistory],
      });
    }
  }, [queue]);
  const { data: history, isLoading: historyLoading } =
    useStockReconciliationHistory(
      {
        customerId: historyCustomerId,
        locationId: historyLocationId,
        skip: (historyPage - 1) * historyPageSize,
        take: historyPageSize,
      },
      { enabled: isHttpAuthReady },
    );

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: customer.customerId,
        label: customer.customerName,
      })),
    [customers],
  );
  const locationOptions = useMemo(
    () =>
      locations.map((location) => ({
        value: location.locationId,
        label: location.locationName,
      })),
    [locations],
  );

  const clearFile = () => {
    parseRequest.current += 1;
    setFileList([]);
    setParsed(undefined);
    setParsing(false);
    form.setFieldsValue({
      vinColumn: undefined,
      locationColumn: undefined,
      modelColumn: undefined,
    });
  };

  const loadFile = async (file: File) => {
    const request = ++parseRequest.current;
    setParsing(true);
    setParsed(undefined);
    form.setFieldsValue({
      vinColumn: undefined,
      locationColumn: undefined,
      modelColumn: undefined,
    });
    try {
      const next = await parseStockFile(file);
      if (parseRequest.current !== request) return;
      if (!next.headers.length || !next.rows.length) {
        message.error(t('stockReconciliationInvalidFile'));
        setFileList([]);
        return;
      }
      setParsed(next);
      form.setFieldsValue({
        vinColumn: next.columns.vin,
        locationColumn: next.columns.location,
        modelColumn: next.columns.model,
      });
    } catch {
      if (parseRequest.current !== request) return;
      message.error(t('stockReconciliationInvalidFile'));
      setFileList([]);
    } finally {
      if (parseRequest.current === request) setParsing(false);
    }
  };

  const columnOptions = (current?: string) => {
    const taken = [vinColumn, locationColumn, modelColumn].filter(
      (column) => column && column !== current,
    );
    return (parsed?.headers ?? [])
      .filter((header) => !taken.includes(header))
      .map((header) => ({ value: header, label: header }));
  };

  const isCompletedRun = (record: StockReconciliationHistoryItem) =>
    !record.status || record.status === 'Completed';

  const filteredQueue = useMemo(
    () =>
      (queue?.items ?? []).filter((item) => {
        if (historyCustomerId && item.customerId !== historyCustomerId) {
          return false;
        }
        if (historyLocationId && item.locationId !== historyLocationId) {
          return false;
        }
        return true;
      }),
    [historyCustomerId, historyLocationId, queue],
  );
  const tableItems = useMemo(
    () => [...filteredQueue, ...(history?.items ?? [])],
    [filteredQueue, history],
  );

  const columns: CustomColumns<StockReconciliationHistoryItem> = [
    {
      title: t('stockReconciliationCustomer'),
      dataIndex: 'customerName',
      fixed: 'left',
      width: 100,
    },
    {
      title: t('stockReconciliationSite'),
      dataIndex: 'locationName',
      fixed: 'left',
      width: 180,
    },
    {
      title: t('stockReconciliationTime'),
      dataIndex: 'reconciliationAt',
      render: (value: string) =>
        value ? dayjs(value).format('DD MMM YYYY hh A') : '—',
      fixed: 'left',
      width: 180,
    },
    {
      title: t('stockReconciliationFile'),
      dataIndex: 'fileName',
      render: (value?: string) => value || '—',
      width: 220,
    },
    {
      title: t('stockReconciliationStatus'),
      dataIndex: 'status',
      width: 300,
      render: (_, record) => {
        const total = record.totalCount || 0;
        const done = isCompletedRun(record)
          ? (record.completedCount ?? total)
          : record.completedCount || 0;
        const percent =
          total === 0
            ? isCompletedRun(record)
              ? 100
              : 0
            : Math.round((done / total) * 100);
        const status = record.status || 'Completed';
        return (
          <div className="d-flex align-items-center w-100 gap-2">
            <Tag
              className="m-0 d-inline-flex justify-content-center"
              style={{ width: 110, flex: '0 0 110px' }}
              color={
                status === 'Failed'
                  ? 'red'
                  : status === 'Processing' || status === 'Uploading'
                    ? 'blue'
                    : status === 'Completed'
                      ? 'green'
                      : 'default'
              }
            >
              {status === 'Failed'
                ? t('stockReconciliationFailed')
                : status === 'Uploading'
                  ? t('stockReconciliationUploadStatus')
                  : status === 'Processing'
                    ? t('stockReconciliationProcessing')
                    : status === 'Pending'
                      ? t('stockReconciliationPending')
                      : t('stockReconciliationCompleted')}
            </Tag>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Progress percent={percent} size="small" />
            </div>
          </div>
        );
      },
    },
    {
      dataIndex: 'completedCount',
      title: t('stockReconciliationProgress'),
      width: 120,
      render: (_, record) => {
        const total = record.totalCount || 0;
        const done = isCompletedRun(record)
          ? (record.completedCount ?? total)
          : record.completedCount || 0;
        return (
          <span>
            {done.toLocaleString()} / {total.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: t('stockReconciliationMessage'),
      dataIndex: 'message',
      render: (value?: string) => value || '—',
      width: 150,
    },
    {
      title: t('stockReconciliationSavedAt'),
      dataIndex: 'createdAt',
      width: 180,
      render: (value: string) =>
        value ? dayjs(value).format('DD MMM YYYY HH:mm') : '—',
    },
    {
      title: t('stockReconciliationSavedBy'),
      dataIndex: 'createdBy',
      width: 150,
      render: (value?: string) => value || '—',
    },
    {
      dataIndex: 'id',
      title: t('stockReconciliationOpen'),
      align: 'center',
      fixed: 'right',
      width: 100,
      render: (_, record) =>
        isCompletedRun(record) ? (
          <Button
            type="link"
            className="p-0"
            onClick={(event) => {
              event.stopPropagation();
              onOpenRun(record.id);
            }}
          >
            {t('stockReconciliationOpen')}
          </Button>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <PageContainer>
      <Title title={t('stockReconciliation')} icon={faClipboardCheck} />
      {error ? (
        <Alert type="error" showIcon message={error} className="mb-3" />
      ) : null}
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            const file = fileList[0]?.originFileObj;
            if (!file) return;
            const customer = customers.find(
              (item) => item.customerId === values.customerId,
            );
            const location = locations.find(
              (item) => item.locationId === values.locationId,
            );
            if (!parsed || !values.vinColumn) return;
            onSubmit({
              customerId: values.customerId,
              customerName: customer?.customerName ?? String(values.customerId),
              locationId: values.locationId,
              locationName: location?.locationName ?? String(values.locationId),
              reconciliationAt: values.reconciliationAt
                .startOf('hour')
                .toISOString(),
              file,
              rows: parsed.rows,
              columns: {
                vin: values.vinColumn,
                location: values.locationColumn,
                model: values.modelColumn,
              },
            });
          }}
        >
          <Row gutter={[16, 0]} wrap={false} style={{ overflowX: 'auto' }}>
            <Col flex="0 0 280px">
              <Form.Item
                name="customerId"
                label={t('stockReconciliationCustomer')}
                rules={[{ required: true }]}
              >
                <Select
                  options={customerOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col flex="0 0 280px">
              <Form.Item
                name="locationId"
                label={t('stockReconciliationSite')}
                rules={[{ required: true }]}
              >
                <Select
                  options={locationOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col flex="1 1 250px">
              <Form.Item
                name="reconciliationAt"
                label={t('stockReconciliationTime')}
                rules={[{ required: true }]}
                initialValue={dayjs().hour(6).minute(0).second(0)}
              >
                <DatePicker
                  showTime={{
                    format: 'hh A',
                    use12Hours: true,
                    showMinute: false,
                    showSecond: false,
                  }}
                  format="DD/MM/YYYY hh A"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col flex="none">
              <Form.Item label={t('stockReconciliationFile')} required>
                <Space size={12}>
                  <Upload
                    accept=".csv,.xlsx,.xls"
                    maxCount={1}
                    fileList={fileList}
                    showUploadList={false}
                    beforeUpload={(file) => {
                      parseRequest.current += 1;
                      setParsed(undefined);
                      setParsing(false);
                      form.setFieldsValue({
                        vinColumn: undefined,
                        locationColumn: undefined,
                        modelColumn: undefined,
                      });
                      setFileList([
                        {
                          uid: file.uid,
                          name: file.name,
                          status: 'done',
                          originFileObj: file,
                        },
                      ]);
                      return false;
                    }}
                  >
                    <Button>{t('stockReconciliationUpload')}</Button>
                  </Upload>
                  <Button
                    type="primary"
                    htmlType="button"
                    loading={parsing}
                    disabled={!fileList.length || parsing}
                    onClick={() => {
                      const file = fileList[0]?.originFileObj;
                      if (file) void loadFile(file);
                    }}
                  >
                    {t('stockReconciliationCheckFile')}
                  </Button>
                </Space>
                {fileList[0] ? (
                  <SelectedFile>
                    <span>{fileList[0].name}</span>
                    <Button type="link" size="small" onClick={clearFile}>
                      {t('stockReconciliationRemoveFile')}
                    </Button>
                  </SelectedFile>
                ) : null}
              </Form.Item>
            </Col>
          </Row>
          {parsed ? (
            <>
              <Divider className="mt-0">
                {t('stockReconciliationMatchColumns')}
              </Divider>
              <Row gutter={[16, 0]}>
                <Col flex="1 1 200px">
                  <Form.Item
                    name="vinColumn"
                    label={t('stockReconciliationVinColumn')}
                    rules={[
                      {
                        required: true,
                        message: t('stockReconciliationChooseVin'),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t('stockReconciliationChooseVin')}
                      options={columnOptions(vinColumn)}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col flex="1 1 200px">
                  <Form.Item
                    name="locationColumn"
                    label={t('stockReconciliationLocationColumn')}
                  >
                    <Select
                      allowClear
                      placeholder={t('stockReconciliationChooseColumn')}
                      options={columnOptions(locationColumn)}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col flex="1 1 200px">
                  <Form.Item
                    name="modelColumn"
                    label={t('stockReconciliationModelColumn')}
                  >
                    <Select
                      allowClear
                      placeholder={t('stockReconciliationChooseColumn')}
                      options={columnOptions(modelColumn)}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col flex="none">
                  <Form.Item label=" ">
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      disabled={!vinColumn}
                    >
                      {submitLabel || t('stockReconciliationRun')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : null}
        </Form>
      </Card>
      <Card
        title={t('stockReconciliationHistory')}
        extra={
          <Space>
            <Select
              allowClear
              placeholder={t('stockReconciliationAllCustomers')}
              options={customerOptions}
              value={historyCustomerId}
              onChange={(value) => {
                setHistoryCustomerId(value);
                setHistoryPage(1);
              }}
              style={{ minWidth: 180 }}
              showSearch
              optionFilterProp="label"
            />
            <Select
              allowClear
              placeholder={t('stockReconciliationAllSites')}
              options={locationOptions}
              value={historyLocationId}
              onChange={(value) => {
                setHistoryLocationId(value);
                setHistoryPage(1);
              }}
              style={{ minWidth: 180 }}
              showSearch
              optionFilterProp="label"
            />
          </Space>
        }
      >
        <CustomTable
          rowKey="id"
          loading={historyLoading}
          columns={columns}
          dataSource={tableItems}
          locale={{ emptyText: t('stockReconciliationNoHistory') }}
          pagination={{
            current: historyPage,
            pageSize: historyPageSize + filteredQueue.length,
            total: (history?.total ?? 0) + filteredQueue.length,
            showSizeChanger: false,
          }}
          onChange={(pagination) => setHistoryPage(pagination.current ?? 1)}
          onRow={(record) =>
            isCompletedRun(record)
              ? {
                  onClick: () => onOpenRun(record.id),
                  style: { cursor: 'pointer' },
                }
              : {}
          }
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </PageContainer>
  );
};
